import { CHECK_VALUE, INPUT_ALIASES, parsePassCode, TOTAL_SYMBOLS } from '@sentra/contracts';
import { useEffect, useRef, useState } from 'react';

/**
 * AD-32: a scan is captured globally, and intercepted only when it is a valid code.
 *
 * A keyboard-wedge scanner types into whatever has focus. Without this, a scan lands in the notes
 * textarea of a half-filled form and writes a live pass code into a person's record; and a scan on
 * a screen with no focused input silently does nothing. So a capture-phase `keydown` listener
 * buffers fast bursts and takes the input ONLY when the buffer is a structurally valid pass code.
 * Anything else is put back into the focused element, in order.
 *
 * **The core is a pure state machine over `{ key, timeStamp }`**, ported from the Phase 0.2
 * measurement tree, which has since been removed, and re-pointed at the contracts package's own
 * codec, so the predicate
 * the interceptor waits on is the same parser the server validates with. Keeping it free of the DOM
 * is what makes the timing testable: a burst is defined by the gaps between keys, and a test that
 * had to produce real gaps would be a test of `setTimeout`.
 *
 * **What is replayed is the ORIGINAL keystrokes, never the normalized form.** The spike found this
 * by writing the test first: replaying the normalized buffer turns `Iolanda`, typed quickly, into
 * `101ANDA` in the operator's notes, because the `O`-to-`0` and `I`/`L`-to-`1` folding that makes a
 * CODE typeable must never be applied to text somebody meant as text.
 *
 * **Three corrections to the spike, each with a reason.**
 *
 * 1. **The CURRENT key is never part of a replay.** The spike appended it to the list and expected
 *    the caller to re-dispatch it, which cannot work in a browser: a synthetic `KeyboardEvent` is
 *    untrusted and inserts nothing, so a replay ending in `Enter` would neither type a newline nor
 *    submit a form. Instead a `replay` decision means "put these back, and let the current key
 *    through", so the terminator keeps its own default action — a newline in a textarea, a submit
 *    in a form — and the order is still right, because a capture-phase handler runs before the
 *    browser applies that default. The one case where both must happen at once (a slow key that
 *    releases the buffer AND starts a new burst) is carried by {@link ScanReplay.holdsCurrent}.
 * 2. **An editing or navigation key breaks the burst; a modifier does not.** The spike let every
 *    multi-character key through untouched, so pressing `Backspace` or `ArrowLeft` with keys still
 *    withheld would have re-inserted them afterwards, in the wrong place. `Shift` still has to be
 *    transparent, because a wedge sends one with every upper-case symbol and treating it as a
 *    breaker would make every scan a replay.
 * 3. **A trailing key is released by a timer.** Without one the last character an operator typed
 *    stays withheld until they type another, which is the difference between "the text lags by one
 *    character while you type" and "the last character never appears". See {@link FLUSH_DELAY_MS}.
 *
 * The residual cost is stated rather than hidden: no global interceptor can tell the first
 * character of a burst from the first character of typing until the second arrives, so a withheld
 * key reaches the focused element one decision late — bounded by the flush timer rather than by the
 * next keystroke. That is also why the gate screen keeps a focused field of its own.
 */

/**
 * The largest gap between two keys of ONE transmission.
 *
 * **Not "faster than a human types".** That was the old rule, it was thirty milliseconds, and it is
 * what made this capture refuse real hardware: a scanner's inter-character delay is a CONFIGURED
 * property, and the configurable range is far wider than any burst. Honeywell's USB-HID hand-held
 * readers program it in ninety-nine steps of five milliseconds, a range of 0 to 495 ms; Zebra's
 * keyboard wedge offers No Delay, Medium at 20 ms and Long at 40 ms, plus an Intra-keystroke Delay
 * that adds a further gap and forces the first to a five-millisecond minimum. So a Zebra on Long
 * Delay missed the old window by ten milliseconds and every scan it sent was replayed into the page
 * as text. Five hundred covers the whole of Honeywell's range and every Zebra setting, with USB HID
 * polling, Bluetooth connection intervals and remote-desktop transports comfortably inside it.
 *
 * Widening it costs nothing, because timing is no longer what decides that a run is a code: the
 * codec is. See {@link ScanCapture}.
 */
export const SCAN_RUN_GAP_MS = 500;

/**
 * A gap this small was produced by a device rather than by fingers.
 *
 * **Diagnostics only. It decides nothing.** Two earlier designs used it and both were wrong. As a
 * flush-delay selector it breaks the devices this module exists for: a reader configured at a
 * 200 ms intercharacter delay sends its second character 200 ms after its first, so a short timer
 * would release the buffer before that character arrived and the code would never assemble, and
 * inverting the test fails identically. As half of the ATTRIBUTION test it put a cliff at 61 ms a
 * character: a uniformly slow reader never produces a gap this small, so every movement from the
 * customer's own hardware would have been filed `manual_key`, permanently, on rows that admit no
 * DELETE, and a suite whose fixture bursts at five milliseconds could never see it. Cadence cannot
 * tell a slow scanner from a human. What it is good for is SHOWING an installer that the reader in
 * front of them is slow, which is what the scanner-check panel uses it for.
 */
export const MACHINE_MAX_GAP_MS = 60;

/**
 * A whole code assembled this fast was produced by a device. **This is the whole attribution test.**
 *
 * A total rather than a per-gap test, and deliberately the ONLY one: a per-gap conjunct puts a cliff
 * at sixty-one milliseconds a character, below which a correctly-configured reader is called a
 * device and above which the same reader is called a person, which is a silent, permanent error on
 * an append-only row for any installation whose hardware sits on the wrong side of it. A total has
 * no such edge inside the range that matters.
 *
 * Three thousand milliseconds is a hundred and forty-two milliseconds a character. Every reader in
 * the documented range is under it, including a chunked one: eight fast, three hundred, eight fast,
 * three hundred, five fast is under a second in total. Transcribing twenty-one random symbols by
 * hand runs two to three hundred milliseconds a character, so four to six seconds, which leaves a
 * margin of roughly one and a half to two. A reader configured slower than that is filed
 * `manual_key`, which is an honest under-claim, and the scanner-check panel is what shows an
 * installer their device is in that state.
 */
export const MACHINE_MAX_TOTAL_MS = 3000;

/**
 * How long after the last key a withheld buffer is released.
 *
 * One value, strictly greater than {@link SCAN_RUN_GAP_MS}, so the run-gap check always fires first
 * and the two can never disagree about where a run ended.
 *
 * **The residual cost, stated rather than hidden.** A character withheld on the gate screen now
 * reaches the focused element up to six hundred milliseconds late. That is paid only where this
 * capture is ARMED, which is the gate screen alone, and the gate screen's only two inputs are the
 * dedicated code field, which opts out, and a file input, which takes no text. A screen that arms
 * this capture in future must either mark its text fields `data-scan-capture="off"` or accept the
 * hold.
 */
export const FLUSH_DELAY_MS = 600;

/**
 * Characters tolerated around the twenty-one, for a scanner configured with a prefix.
 *
 * A prefix pushes the code off the head of the run, so the head never parses; the trailing
 * twenty-one symbols are tried as well, which is what finds it. Bounded rather than a free sliding
 * window, and the bound is arithmetic rather than taste: **each extra character is another parse
 * attempt at another near-independent check residue**, so the chance a run of alphabet characters
 * yields a false code is `1 - (36/37)^n` over `n` windows. At an allowance of four that is five
 * windows and 12.8%; at two it is three windows and 7.9%. Two is enough, because a prefix outside
 * the alphabet never enters the buffer at all, so this only has to cover prefixes drawn from the
 * code's own symbols.
 *
 * **And a false positive is cheap rather than free.** It resolves to a credential that does not
 * exist, so nobody is admitted; but the resolve writes a `scan_attempts` row, which is the source
 * of Section 1.12's unknown-scan signal, and it spends the resolve rate-limit budget.
 */
export const SCAN_DECORATION_ALLOWANCE = 2;

/**
 * An `Enter` arriving this soon after a submit belongs to that scan, and is swallowed.
 *
 * A code is now submitted the moment it parses, so a scanner's suffix arrives AFTER the submission
 * rather than causing it. Left alone it would reach the page, and on the resolve panel it would
 * activate whatever control has focus and record a movement nobody chose.
 *
 * **`Tab` is deliberately not swallowed, though it is also a terminator.** It would eat the first
 * Tab an operator presses in the half second after a scan, which is exactly when they are reaching
 * for the direction button, and the whole gate path has to stay completable from the keyboard. The
 * asymmetry is justified by consequence: a stray `Tab` moves focus, which is recoverable and
 * invisible, while a stray `Enter` writes a row.
 */
export const TERMINATOR_GRACE_MS = 500;

/**
 * AD-30 at the input: a hardware double read of the same code inside this window is one physical
 * act and is suppressed before it becomes a request. The DEFAULT only; the live value is the
 * `scan.client_burst_dedupe_ms` setting, which reaches the browser through `GET /api/v1/config`.
 *
 * It is deliberately NOT the server's duplicate rule. The server answers "already registered N
 * seconds ago" for a replayed idempotency key, which is a different event with a different cause;
 * this is the scanner reading one band twice.
 *
 * **Raised from four hundred, on sourced grounds.** Honeywell's reread delay, which is how long a
 * reader waits before reading the same symbol again, DEFAULTS to 750 ms, with 500, 750, 1000 and
 * 2000 selectable. At four hundred, every re-read of a band left in front of a presentation-mode
 * scanner fell outside the window and was submitted as a fresh scan. Fifteen hundred covers the
 * default and the settings either side of it. Not longer: a suppressed duplicate is deliberately
 * silent, and a window beyond an operator's own patience makes a deliberate rescan look like a dead
 * terminal.
 */
export const DUPLICATE_WINDOW_MS = 1500;

/** One key press, as the state machine sees it. */
export interface ScanKey {
  readonly key: string;
  /** Milliseconds on a monotonic clock. The EVENT's own stamp, never the handler's wall clock. */
  readonly timeStamp: number;
  /**
   * Whether a modifier is being held, which means this is a COMMAND rather than a character.
   *
   * A wedge scanner never chords: it sends a symbol, possibly with `Shift`, and a terminator. So a
   * key held with Control, Command or Alt is somebody reaching for a shortcut, and intercepting it
   * would take the shortcut away AND, one flush later, insert a stray letter into whatever they
   * were typing. Computed at the DOM layer by {@link chordedKey}, so this file's decisions stay
   * testable without an event.
   */
  readonly chorded: boolean;
  /**
   * The code symbol this key produces, when `key` itself is not one.
   *
   * A scanner emits scancodes and the operating system maps them through whatever keyboard layout
   * the terminal is running, so a reader programmed for one layout against a host set to another
   * produces the wrong letters. `event.code` names the PHYSICAL key and is layout-independent, so
   * the DOM layer resolves it and hands the result down. Resolved there rather than here for the
   * same reason `chorded` is: this file never touches an event.
   *
   * **What is REPLAYED is still the original `key`.** The recovery makes a code readable; it must
   * never rewrite text somebody meant as text.
   *
   * **Two device configurations this cannot rescue, stated rather than left to be discovered.** A
   * reader set to ALT-mode transmission holds `Alt` on every character, which {@link chordedKey}
   * reads as a command and drains on, so such a device can never be assembled here; treating a held
   * `Alt` as a character would take every Alt shortcut away from the operator, which is worse. And
   * a reader configured to transmit the GROUPED form sends hyphens, which are not code characters
   * and so break the run, even though the codec itself tolerates them. Both are scanner settings
   * rather than product defects, and the scanner-check panel is what tells an installer which one
   * they are looking at: the modifier column for the first, a symbol column reading nothing for the
   * second.
   */
  readonly symbol?: string;
  /**
   * How this character reached the page, when it did not come from a key.
   *
   * Applied by {@link ScanCapture.take} at the moment the character is HELD, which is the only
   * moment the run it belongs to is settled: set any earlier and the first character's own restart
   * wipes it, set any later and the code has already completed and been attributed.
   */
  readonly arrival?: ScanArrival;
}

/**
 * Where a character came from, when that changes what the run may be called.
 *
 * Only a paste does. An insertion needs no marker: every character of one shares a single
 * timestamp, so the run's total elapsed time is near zero and the attribution test calls it a
 * device on its own evidence, which is correct — nobody types twenty-one characters into one event.
 * A paste would qualify the same way and must not, because a person pasting a code is a person, and
 * claiming hardware for it would put a lie in a table that admits no DELETE.
 */
export type ScanArrival = 'other' | 'paste';

/**
 * What a text arrival asks of its caller.
 *
 * Three separate facts, because the text path cannot express them as one decision: what the run
 * decided, what the field is owed, and whether the event must be cancelled.
 */
export interface TextArrival {
  readonly decision: ScanDecision;
  /** Characters to put into the focused element, in order. */
  readonly replay: readonly string[];
  /** Whether the caller must prevent the event's default action. */
  readonly cancel: boolean;
}

/** Keys that are put back into the focused element, and what happens to the current one. */
interface ScanReplay {
  readonly action: 'replay';
  readonly keys: readonly string[];
  /**
   * Whether the key that produced this decision is itself withheld.
   *
   * True only when a slow key both released the previous buffer and started a new one, which is a
   * real sequence: somebody types a letter, pauses, and then a scanner fires.
   */
  readonly holdsCurrent: boolean;
}

/**
 * Who produced a run, for AD-53's method.
 *
 * `device` is claimed on ONE piece of evidence: the whole run finished inside
 * {@link MACHINE_MAX_TOTAL_MS}. There is deliberately no per-gap conjunct — {@link MACHINE_MAX_GAP_MS}
 * decides nothing, and its own note records why that design was rejected. Everything else is
 * `manual`, so the attribution under-claims and never over-claims. A reader configured so slowly
 * that a whole code takes over three seconds is recorded as manual, which is honest rather than
 * wrong, and the scanner-check panel is what shows an installer that their device is in that state.
 */
export type ScanOrigin = 'device' | 'manual';

export type ScanDecision =
  /** Nothing is withheld and nothing is owed; let the key through. */
  | { readonly action: 'passthrough' }
  /** The key is withheld. The caller must prevent its default action. */
  | { readonly action: 'buffer' }
  /** A complete, valid code. The caller must prevent the key's default action. */
  | { readonly action: 'submit'; readonly code: string; readonly origin: ScanOrigin }
  /** The same code again inside the duplicate window. Suppressed, not submitted. */
  | {
      readonly action: 'duplicate';
      readonly code: string;
      readonly sinceMs: number;
      readonly origin: ScanOrigin;
    }
  /** A scanner's own suffix, arriving after its code was already taken. Cancel it, do nothing. */
  | { readonly action: 'swallow' }
  | ScanReplay;

/**
 * The keys a wedge sends alongside a symbol, which must not break a burst.
 *
 * `Shift` is the one that matters: a scanner emits it with every upper-case character, so treating
 * it as a run breaker would make every scan a replay. The rest are here because a terminal may have
 * a sticky modifier or an IME in the way, and none of them types anything.
 */
const TRANSPARENT_KEYS: ReadonlySet<string> = new Set([
  'Alt',
  'AltGraph',
  'CapsLock',
  'Control',
  'Dead',
  'Meta',
  'NumLock',
  'Process',
  'ScrollLock',
  'Shift',
  'Unidentified',
]);

/** The two keys a wedge is configured to send after the payload. */
const TERMINATORS: ReadonlySet<string> = new Set(['Enter', 'Tab']);

/**
 * Whether a key press is a COMMAND rather than a character.
 *
 * `AltGraph` is the exception that has to be written down: on several European layouts it is how a
 * character is typed at all, and the browser reports it as Control AND Alt held together. Treating
 * that as a chord would be harmless here, since a scanner does not use it, but treating a REAL
 * chord as a character is what takes Control-K away from the operator, so the test is exact rather
 * than approximate.
 */
function chordedKey(event: KeyboardEvent): boolean {
  if (event.getModifierState('AltGraph')) {
    return false;
  }
  return event.ctrlKey || event.metaKey || event.altKey;
}

/**
 * Whether a character could be part of a code.
 *
 * Derived from the codec's own tables rather than written as a regular expression: the check
 * alphabet is the wider of the two, and the aliases are the glyphs an operator may type for a
 * symbol that is not in it. A hand-written character class would be a second statement of the
 * alphabet, and the day a symbol was added to one it would be missing from the other.
 */
export function isCodeCharacter(key: string): boolean {
  if (key.length !== 1) {
    return false;
  }
  const upper = key.toUpperCase();
  return CHECK_VALUE.has(upper) || INPUT_ALIASES.has(upper);
}

/**
 * How close a `beforeinput` has to be to a keystroke to be that keystroke's own echo.
 *
 * The two are dispatched from one input event, so their stamps are the same to within rounding.
 * Anything later is a separate delivery.
 */
const ECHO_WINDOW_MS = 2;

/** Matches the physical letter and digit keys, whose names carry the symbol they stand for. */
const PHYSICAL_SYMBOL = /^(?:Key([A-Z])|Digit(\d))$/u;

/**
 * The code symbol a physical key stands for, when the character it produced is not one.
 *
 * Only consulted when `event.key` has failed, so a correctly configured terminal never reaches it,
 * and only for a key that produced SOME single character, so a shortcut or a navigation key cannot
 * be turned into a symbol. `Numpad` keys are deliberately absent: with NumLock off they arrive as
 * `ArrowLeft` and friends, and rescuing those would weaken the rule that an editing key ends a run.
 */
export function physicalSymbol(key: string, code: string): string | undefined {
  if (key.length !== 1 || isCodeCharacter(key)) {
    return undefined;
  }
  const match = PHYSICAL_SYMBOL.exec(code);
  const symbol = match?.[1] ?? match?.[2];
  return symbol !== undefined && isCodeCharacter(symbol) ? symbol : undefined;
}

/** What a run produced, for the scanner-check panel. Never read by the product's own behaviour. */
export interface ScanObservation {
  /** The character the page received, or the key's name when it is not a character. */
  readonly key: string;
  /** The physical key, when the DOM layer knew it. */
  readonly code: string | null;
  /** Milliseconds since the previous arrival of this run, or `null` for the first. */
  readonly gapMs: number | null;
  /** The symbol it contributed to the buffer, or `null` when it contributed none. */
  readonly symbol: string | null;
  /** How it arrived. */
  readonly via: 'key' | 'insert' | 'paste';
  /**
   * Whether a modifier was held.
   *
   * Here because a reader set to ALT-mode transmission holds `Alt` on every character, which this
   * capture reads as a command and refuses. Without this column such a device looks, on the panel,
   * exactly like one sending nothing at all.
   */
  readonly chorded: boolean;
  /** What the machine decided. */
  readonly action: ScanDecision['action'];
}

export interface ScanCaptureOptions {
  readonly maxGapMs?: number;
  readonly duplicateWindowMs?: number;
}

/** One held character: what the page would have received, and what it means to the codec. */
interface HeldSymbol {
  readonly original: string;
  readonly symbol: string;
}

/**
 * The state machine. One instance per armed screen; it holds the withheld keys and nothing else.
 *
 * **The rule it embodies, which is the whole of this change: timing decides how long to WITHHOLD
 * characters, and the codec decides whether they are a code.** `parsePassCode` answers length,
 * alphabet and a modulo-37 check symbol together, so a run that satisfies it is a code whatever
 * cadence delivered it, and a run that does not is not one however fast it arrived. That is why the
 * run window can be generous: the filter it replaced was never the thing keeping the operator's
 * typing safe.
 *
 * A code is therefore taken the MOMENT it parses, with no terminator required, which is what makes
 * a reader configured with no suffix work, and what makes a reader whose suffix arrives late stop
 * mattering. The suffix, when one comes, is swallowed by {@link TERMINATOR_GRACE_MS}.
 */
export class ScanCapture {
  private readonly maxGapMs: number;

  private readonly duplicateWindowMs: number;

  private buffer: HeldSymbol[] = [];

  /**
   * Whether this run of keys has already disqualified itself.
   *
   * Set when a run passes the twenty-one symbols of a code plus the decoration allowance. Nothing a
   * scanner sends does that, so what produced it was a paste or a stuck key, and without this the
   * tail of a long run would start a fresh buffer inside the same run and could, on exactly the
   * wrong characters, submit a code nobody scanned. A gap longer than the run window clears it,
   * because that is where one run of keys ends and the next begins.
   */
  private poisoned = false;

  private lastKeyAt = Number.NEGATIVE_INFINITY;

  /** When the first character still held was taken, for the machine-cadence test. */
  private runStartedAt = Number.NEGATIVE_INFINITY;

  /**
   * Whether this run carries characters that arrived by paste.
   *
   * It has to be tracked rather than inferred, because every character of one arrival shares a
   * single timestamp: the gaps between them are zero, which is the strongest machine cadence there
   * is, and attribution read off them alone would call every paste a device.
   */
  private pasted = false;

  private lastCode: string | null = null;

  private lastCodeAt = Number.NEGATIVE_INFINITY;

  /** When a code was last taken, so that scanner's own suffix can be recognised and swallowed. */
  private lastSubmitAt = Number.NEGATIVE_INFINITY;

  constructor(options: ScanCaptureOptions = {}) {
    this.maxGapMs = options.maxGapMs ?? SCAN_RUN_GAP_MS;
    this.duplicateWindowMs = options.duplicateWindowMs ?? DUPLICATE_WINDOW_MS;
  }

  /** What is currently withheld from the focused element. Empty when nothing is. */
  get pending(): readonly string[] {
    return this.buffer.map((held) => held.original);
  }

  /**
   * Releases the buffer because no further key arrived.
   *
   * **A pure replay: it deliberately does not attempt a parse.** Every append already tries one, at
   * every length the buffer has held, so by the time this timer fires a parseable buffer cannot
   * exist and a parse here would be an unreachable branch that no test could turn red. Keeping it
   * out has a second payoff: every submission therefore happens inside a real input handler rather
   * than from a timer, which is what keeps the gate's alarm priming inside the operator's own
   * gesture, where a browser's autoplay policy requires it.
   */
  flush(): ScanDecision {
    return this.drain(false);
  }

  /** Forgets the run in flight, for a screen that is being unmounted or suppressed. */
  reset(): void {
    this.buffer = [];
    this.lastKeyAt = Number.NEGATIVE_INFINITY;
    this.runStartedAt = Number.NEGATIVE_INFINITY;
    this.pasted = false;
    this.poisoned = false;
  }

  handleKeyDown(event: ScanKey): ScanDecision {
    if (TRANSPARENT_KEYS.has(event.key)) {
      return { action: 'passthrough' };
    }

    // A shortcut, not a character. Whatever was withheld is put back FIRST, so the shortcut acts on
    // the text as the operator left it rather than on a field that is one flush behind, and the
    // chord itself is never withheld: a capture-phase listener that swallowed Control-K would make
    // this product's own command palette unreachable from the screen that needs it most.
    if (event.chorded) {
      return this.drain(false);
    }

    if (TERMINATORS.has(event.key)) {
      // A scanner's own suffix, arriving after its code was taken on the twenty-first symbol. Only
      // `Enter` is swallowed; see {@link TERMINATOR_GRACE_MS} for why `Tab` is let through.
      if (event.key === 'Enter' && event.timeStamp - this.lastSubmitAt <= TERMINATOR_GRACE_MS) {
        return { action: 'swallow' };
      }
      // Otherwise it ends whatever was in flight. It cannot COMPLETE one: every character already
      // asks the codec at every length the buffer has held, so a parseable buffer cannot survive to
      // here, which is the same argument `flush()` makes for not parsing either.
      return this.buffer.length === 0 ? { action: 'passthrough' } : this.drain(false);
    }

    // Any other multi-character key edits or moves: `Backspace`, `ArrowLeft`, `Delete`, `Escape`,
    // `F5`. Releasing the buffer first is what keeps the order right, because otherwise the
    // withheld characters would be inserted after the caret had already moved.
    if (event.key.length > 1) {
      return this.drain(false);
    }

    const symbol = isCodeCharacter(event.key) ? event.key : event.symbol;
    const gap = event.timeStamp - this.lastKeyAt;

    if (gap > this.maxGapMs) {
      return this.restart(event, symbol);
    }

    if (this.poisoned) {
      // The stamp moves with every key of the run, or the measured gap ACCUMULATES from the key
      // that poisoned it and the branch above fires later, restarting the buffer inside the very
      // run this flag exists to disqualify.
      this.lastKeyAt = event.timeStamp;
      return { action: 'passthrough' };
    }

    if (symbol === undefined) {
      return this.buffer.length === 0 ? { action: 'passthrough' } : this.drain(false);
    }

    return this.take(event.key, symbol, event.timeStamp, event.arrival);
  }

  /**
   * Characters that arrived as text rather than as keystrokes.
   *
   * A reader in clipboard mode, a software keyboard and an IME all deliver a code without producing
   * a usable `keydown` for any of it, so a capture that listened only for keys saw nothing at all
   * and the code landed in the page. The text goes through the SAME buffer and the same ceiling as
   * a keystroke, so a ten-thousand-character paste does not become a ten-thousand-entry buffer: a
   * character outside the alphabet drains, and the run poisons past the allowance.
   *
   * **It cannot return a bare {@link ScanDecision}, because the two paths start from opposite
   * conditions.** A withheld keystroke has not been inserted, so cancelling it is the whole of the
   * work, and a `replay` there means "put the buffer back and let this key through" — the browser
   * inserts the current character itself. Text has not been inserted AT ALL when this decides, so
   * the same word would mean something else: cancel, and put back everything that should land,
   * including the characters of THIS arrival the run did not keep. Collapsing the two produced both
   * failures at once — `Hola ` written to the field twice, and the `HELLO ` of `HELLO WORLD`
   * dropped — so the text path gets a return type that says what it needs.
   */
  handleText(text: string, timeStamp: number, source: 'insert' | 'paste'): TextArrival {
    if (text.length === 0) {
      return { decision: { action: 'passthrough' }, replay: [], cancel: false };
    }
    // The marker travels WITH each character rather than being set up front, because the first
    // character of a run arrives after an unbounded gap and therefore restarts the run, and a
    // restart clears exactly the flags it would have set. See {@link arrivalOf}.
    const arrival: ScanArrival = source === 'paste' ? 'paste' : 'other';
    const replay: string[] = [];
    // Whether this capture took anything at all. A buffer that was already holding characters
    // counts, because those are owed to the field and only a cancellation can deliver them.
    let consumed = this.buffer.length > 0;

    for (const character of text) {
      const decision = this.handleKeyDown({ key: character, timeStamp, chorded: false, arrival });
      if (decision.action === 'submit' || decision.action === 'duplicate') {
        // Whatever this arrival owed the field before the code completed still has to land, or the
        // characters in front of a pasted code disappear.
        return { decision, replay, cancel: true };
      }
      if (decision.action === 'buffer' || decision.action === 'swallow') {
        consumed = true;
      }
      if (decision.action === 'replay') {
        replay.push(...decision.keys);
        // `holdsCurrent` means this character was itself taken into the new run; otherwise it is
        // one the field is owed, and cancelling the event is what stops the browser delivering it.
        if (!decision.holdsCurrent) {
          replay.push(character);
        }
      } else if (decision.action === 'passthrough') {
        replay.push(character);
      }
    }

    // Nothing was consumed, so the event is none of this capture's business: letting it through
    // natively is what keeps undo, the caret and a controlled field behaving as the page expects.
    // **Not "the replay came to the same length as the text"**, which it does whenever a run was
    // held and then released inside one arrival: `Hola ` buffers four characters and gives back
    // five, and reading that as "untouched" let the event through after cancelling nothing, so the
    // characters were dropped.
    if (!consumed) {
      return { decision: { action: 'passthrough' }, replay: [], cancel: false };
    }
    return { decision: { action: 'buffer' }, replay, cancel: true };
  }

  /**
   * Ends the run in flight and begins another with this key.
   *
   * Reached when a key arrives too late to belong to the previous transmission. What was withheld
   * goes back, the previous run's disqualification is forgotten, and this key starts over, so a run
   * that follows a pause is still caught.
   */
  private restart(event: ScanKey, symbol: string | undefined): ScanDecision {
    const released = this.drain(false);
    if (symbol === undefined) {
      return released;
    }
    // The buffer was just emptied, so this character can only be held: a run of one is never a
    // code. The current key therefore both released the previous buffer and started a new one,
    // which is the one case where a replay must also withhold what caused it.
    this.take(event.key, symbol, event.timeStamp, event.arrival);
    return released.action === 'replay'
      ? { action: 'replay', keys: released.keys, holdsCurrent: true }
      : { action: 'buffer' };
  }

  /** Holds one character, then asks the codec whether the run is now a code. */
  private take(original: string, symbol: string, at: number, arrival?: ScanArrival): ScanDecision {
    if (this.buffer.length >= TOTAL_SYMBOLS + SCAN_DECORATION_ALLOWANCE) {
      // Longer than a code plus anything a scanner decorates one with. What follows belongs to the
      // same run of keys, so it is let through rather than treated as the start of a new one.
      const released = this.drain(false);
      this.poisoned = true;
      this.lastKeyAt = at;
      return released;
    }
    if (this.buffer.length === 0) {
      this.runStartedAt = at;
    }
    this.buffer.push({ original, symbol });
    this.lastKeyAt = at;
    if (arrival === 'paste') {
      this.pasted = true;
    }
    return this.complete(at) ?? { action: 'buffer' };
  }

  /**
   * Whether the run is now a code, taking it if so.
   *
   * The trailing twenty-one symbols rather than the whole buffer, which is what finds a code a
   * scanner put a prefix in front of; the head is covered too, because at twenty-one symbols the
   * tail IS the head.
   */
  private complete(at: number): ScanDecision | null {
    if (this.buffer.length < TOTAL_SYMBOLS) {
      return null;
    }
    const tail = this.buffer.slice(-TOTAL_SYMBOLS);
    const parsed = parsePassCode(tail.map((held) => held.symbol).join(''));
    if (!parsed.ok) {
      return null;
    }
    const origin: ScanOrigin =
      !this.pasted && at - this.runStartedAt <= MACHINE_MAX_TOTAL_MS ? 'device' : 'manual';
    this.reset();
    this.lastSubmitAt = at;

    const sinceMs = at - this.lastCodeAt;
    if (this.lastCode === parsed.code && sinceMs < this.duplicateWindowMs) {
      // The window is measured from the LAST read, not from the last accepted one. A scanner that
      // bounces three times at two hundred milliseconds would otherwise have its third read land
      // outside the window and be submitted as a second movement.
      this.lastCodeAt = at;
      return { action: 'duplicate', code: parsed.code, sinceMs, origin };
    }
    this.lastCode = parsed.code;
    this.lastCodeAt = at;
    return { action: 'submit', code: parsed.code, origin };
  }

  private drain(holdsCurrent: boolean): ScanDecision {
    const keys = this.buffer.map((held) => held.original);
    this.reset();
    return keys.length === 0 ? { action: 'passthrough' } : { action: 'replay', keys, holdsCurrent };
  }
}

/**
 * The attribute a control uses to say the keyboard is its own.
 *
 * AD-32 suppresses interception while the dedicated code-entry field owns the keyboard, and this is
 * how a field says so. An attribute rather than a registry of element references, because the
 * suppression has to be decidable from the focused element alone at the moment a key arrives, and
 * because a control three components away can opt out without threading a callback to it.
 */
export const SCAN_OPT_OUT_ATTRIBUTE = 'data-scan-capture';

/** The value {@link SCAN_OPT_OUT_ATTRIBUTE} carries. A bare attribute would be easy to mistype. */
export const SCAN_OPT_OUT = 'off';

/** The roles that mean a dialog owns the keyboard. Radix renders both. */
const MODAL_SELECTOR = '[role="dialog"],[role="alertdialog"]';

/**
 * Whether interception is suppressed right now (AD-32).
 *
 * Two cases, and they are the two the rule names. A field that declares itself a code-entry field
 * takes the keystrokes directly, because a scan there is the operator's own intent and routing it
 * through this machinery would only add a way for it to go wrong. And a modal owns the keyboard by
 * definition: a scan while a confirmation is open must not submit a movement behind it.
 */
export function scanSuppressed(active: Element | null): boolean {
  if (active === null) {
    return false;
  }
  return (
    active.getAttribute(SCAN_OPT_OUT_ATTRIBUTE) === SCAN_OPT_OUT ||
    active.closest(MODAL_SELECTOR) !== null
  );
}

/** The two element types this application can put text back into. */
type TextField = HTMLInputElement | HTMLTextAreaElement;

/**
 * The input types that accept a programmatic value.
 *
 * **An allowlist rather than `instanceof HTMLInputElement`, because a file input throws.** Its
 * `value` setter refuses anything but the empty string, and the gate screen has one: `upload.tsx`
 * renders it for a photographed band. At the old thirty-millisecond window two keystrokes had to
 * land inside it while that input held focus, so it was nearly unreachable; at five hundred it is
 * ordinary. The throw would escape a capture-phase `keydown` listener, which kills every key on the
 * screen the scanner depends on.
 *
 * The types left out are the ones where putting text back is meaningless anyway: a checkbox, a
 * radio, a range, a colour, a button. Dropping their keys is the honest boundary {@link replayInto}
 * already documents for a focus that is not a text field at all.
 */
const TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'date',
  'datetime-local',
  'email',
  'month',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'url',
  'week',
]);

function isTextField(node: Element | null): node is TextField {
  if (node instanceof HTMLTextAreaElement) {
    return true;
  }
  return node instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(node.type);
}

/**
 * The prototype's own `value` setter, which is what makes React notice the change.
 *
 * React tracks the last value it wrote on the node itself and compares against it to decide whether
 * an `input` event is a real change; assigning `element.value` directly updates that tracker too,
 * so React sees no change and a controlled field silently reverts on its next render. Going through
 * the prototype setter bypasses the tracker, which is exactly what a real keystroke does.
 *
 * It is also a method CALL rather than an assignment to a parameter's property, which is what keeps
 * this file outside the `no-param-reassign` exception that `tools/__tests__/eslint-config.test.ts`
 * pins to two named files.
 */
function nativeValueSetter(field: TextField): ((value: string) => void) | null {
  const prototype =
    field instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  // Bound to the element on the spot, which is also what keeps this out of the unbound-method
  // rule's way: an unbound accessor handed around is a `this` waiting to be lost.
  return Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.bind(field) ?? null;
}

/**
 * Puts withheld keystrokes back into the element that would have received them.
 *
 * **At the caret, not at the end.** An operator who moved the caret into the middle of a note and
 * then typed would otherwise find their text appended somewhere else entirely.
 *
 * **Nothing is inserted when the focus is not a text field**, and that is the honest boundary of
 * "a keystroke is never lost": keys withheld while the body had focus were going nowhere anyway.
 * A `contenteditable` is not handled because this application has none; if one is ever added, this
 * is the function that has to learn about it.
 *
 * Returns whether anything was inserted, so a caller can decide about the current key.
 */
export function replayInto(active: Element | null, keys: readonly string[]): boolean {
  if (keys.length === 0 || !isTextField(active)) {
    return false;
  }
  const setValue = nativeValueSetter(active);
  if (setValue === null) {
    return false;
  }

  const text = keys.join('');
  // `selectionStart` is null on the input types that do not support a selection (`number`,
  // `email`, `date`). Appending is the only sensible answer there, and it is what typing does.
  const start = active.selectionStart ?? active.value.length;
  const end = active.selectionEnd ?? start;
  // **Nothing thrown in here may escape, and the reason is not the one a reader expects.** A throw
  // does not remove the listener: the browser reports an uncaught exception and the next keystroke
  // still arrives. What is lost is the REST of the handler. The caller throws part-way through, so
  // the flush timer is never cleared and never re-armed, the indicator is never updated, and the
  // element the keys were taken from is never released; the orphaned timer then fires later and
  // throws again, from a timer, with no event to attach the failure to. The allowlist above removes
  // the case that is KNOWN to throw and this guards the one nobody has met. **It belongs here
  // rather than at the call sites**, because there are two of them and one is that timer.
  //
  // A dropped keystroke is a nuisance; a capture left with a stale timer and a stale indicator is
  // the scanner appearing to work and not working.
  try {
    setValue(`${active.value.slice(0, start)}${text}${active.value.slice(end)}`);
  } catch {
    return false;
  }
  try {
    active.setSelectionRange(start + text.length, start + text.length);
  } catch {
    // Same input types as above: they have a value but no selection to move.
  }
  try {
    active.dispatchEvent(
      new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }),
    );
  } catch {
    // A listener of the page's own threw. The characters are in the field either way.
  }
  return true;
}

/** What the scanner-ready indicator renders, and what the gate screen knows about the keyboard. */
export type ScanCaptureState =
  /** Not armed: this screen does not act on a scan. */
  | 'off'
  /** Armed and idle. A burst now will be intercepted. */
  | 'ready'
  /** Keys are withheld: something is arriving fast enough to be a scan. */
  | 'reading'
  /** A dialog or a code-entry field owns the keyboard, so nothing is intercepted (AD-32). */
  | 'suppressed';

export interface UseScanCaptureOptions {
  /** Whether this screen acts on a scan at all. */
  readonly enabled: boolean;
  /** A complete, valid, non-duplicate code, and the evidence for what to call it. */
  readonly onScan: (code: string, origin: ScanOrigin) => void;
  /** A hardware double read, suppressed before it became a request. */
  readonly onDuplicate?: (code: string) => void;
  /**
   * The live `scan.client_burst_dedupe_ms` setting, which reaches the browser through
   * `GET /api/v1/config`. Omitted, the shipped default applies.
   */
  readonly duplicateWindowMs?: number;
  /**
   * Every arrival, for the scanner-check panel. Undefined when nothing is watching, which is the
   * ordinary case and costs nothing.
   */
  readonly onObserve?: (observation: ScanObservation) => void;
}

/**
 * Installs the capture-phase listeners for as long as the screen is mounted.
 *
 * **Capture phase and on the document**, which is what "captured globally" means: a wedge types
 * into whatever has focus, so a listener bound to one input would see a scan only when that input
 * already had it, which is precisely the case that needs no help.
 *
 * **Three listeners, because a code does not always arrive as keys.** `keydown` is the ordinary
 * path. `beforeinput` catches a software keyboard, an IME and any reader that inserts text without
 * producing a usable key. `paste` is needed as WELL as `beforeinput`, because a paste while nothing
 * is focused fires no `beforeinput` at all, and the gate screen focuses nothing by default.
 *
 * **It is armed by the screen that acts on a scan** rather than by the application root. A scan is
 * an instruction to do something, and the only screen where that instruction has a meaning is the
 * gate: intercepting one on the registration screen would either do nothing or navigate away from a
 * half-filled form. The mechanism is global; the arming is not.
 */
export function useScanCapture(options: UseScanCaptureOptions): ScanCaptureState {
  const { enabled, onScan, onDuplicate, duplicateWindowMs, onObserve } = options;
  const [suppressed, setSuppressed] = useState(false);
  const [reading, setReading] = useState(false);
  // The callbacks and the configured window are read through a ref so that a screen re-rendering
  // with a new closure does not tear the listeners down and build them again, which would drop a
  // run that was halfway through. `onObserve` in particular changes identity every time the
  // scanner-check panel is opened or closed, and the moment somebody opens it is the moment they
  // are about to scan.
  const handlers = useRef({ onScan, onDuplicate, duplicateWindowMs, onObserve });
  useEffect(() => {
    handlers.current = { onScan, onDuplicate, duplicateWindowMs, onObserve };
  }, [duplicateWindowMs, onDuplicate, onObserve, onScan]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const capture = new ScanCapture(
      handlers.current.duplicateWindowMs === undefined
        ? {}
        : { duplicateWindowMs: handlers.current.duplicateWindowMs },
    );
    let timer: ReturnType<typeof setTimeout> | null = null;
    /**
     * The element the withheld keys were taken from.
     *
     * Held, rather than read again when the timer fires: focus can move inside the flush delay, and
     * putting the characters into whatever has focus THEN would insert them into a different field
     * from the one the operator was typing in.
     */
    let withheldFrom: Element | null = null;
    /**
     * When a `keydown` last produced a character the page will insert itself.
     *
     * This is what stops one keystroke being counted twice, once as a key and again as the
     * `beforeinput` it causes. **A stamp rather than a flag**, because a flag is cleared only by
     * the next event that reads it: a key that produced a character the page never inserted — focus
     * on a button, on the body, on a file input — left a boolean armed indefinitely, and the next
     * code a device delivered by insertion was discarded as an echo of a keystroke that had reached
     * nothing. A stamp expires by itself.
     */
    let lastCharacterKeyAt = Number.NEGATIVE_INFINITY;
    let lastObservedAt: number | null = null;

    const clear = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    /**
     * Reports one arrival to the scanner check.
     *
     * **The gap is measured inside a run and never across the quiet between two.** `lastObservedAt`
     * is cleared whenever a run ends, so the first character of the next scan reports no gap at
     * all. Without that, scanning, waiting ten seconds and scanning again made the panel's headline
     * number read ten thousand milliseconds, which an installer would read as a ten-second
     * intercharacter delay: the single figure the panel exists to show, wrong in exactly the
     * situation it exists for.
     */
    const observe = (
      key: string,
      code: string | null,
      at: number,
      via: ScanObservation['via'],
      decision: ScanDecision,
      symbol: string | null,
      startsRun: boolean,
      chorded = false,
    ): void => {
      const watcher = handlers.current.onObserve;
      if (watcher !== undefined) {
        watcher({
          key,
          code,
          gapMs: startsRun || lastObservedAt === null ? null : at - lastObservedAt,
          symbol,
          via,
          chorded,
          action: decision.action,
        });
      }
      lastObservedAt = decision.action === 'buffer' ? at : null;
    };

    /** Releases the trailing key when no further one arrived. See {@link FLUSH_DELAY_MS}. */
    const release = (): void => {
      timer = null;
      const decision = capture.flush();
      if (decision.action === 'replay') {
        replayInto(withheldFrom, decision.keys);
      }
      withheldFrom = null;
      setReading(false);
    };
    /**
     * Applies one decision.
     *
     * **The consumer is not called here.** It comes back as `notify` so the caller can cancel the
     * keystroke FIRST: the gate's `onScan` primes an `AudioContext`, and a throw from that would
     * escape this capture-phase handler with the twenty-first character of a live pass code still
     * on its way into the focused element.
     */
    const apply = (
      decision: ScanDecision,
      active: Element | null,
    ): { readonly held: boolean; readonly notify: (() => void) | null } => {
      switch (decision.action) {
        case 'passthrough': {
          return { held: false, notify: null };
        }
        case 'buffer':
        case 'swallow': {
          return { held: true, notify: null };
        }
        case 'submit': {
          return {
            held: true,
            notify: () => {
              handlers.current.onScan(decision.code, decision.origin);
            },
          };
        }
        case 'duplicate': {
          return {
            held: true,
            notify: () => {
              handlers.current.onDuplicate?.(decision.code);
            },
          };
        }
        case 'replay': {
          replayInto(active, decision.keys);
          return { held: decision.holdsCurrent, notify: null };
        }
      }
    };

    /** Re-arms the flush timer and the indicator after a decision has been applied. */
    const settle = (active: Element | null): void => {
      clear();
      const pending = capture.pending.length > 0;
      setReading(pending);
      withheldFrom = pending ? active : null;
      if (pending) {
        timer = setTimeout(release, FLUSH_DELAY_MS);
      }
    };

    /** Puts back what was withheld when a dialog or the code field takes the keyboard. */
    const surrender = (active: Element | null): void => {
      clear();
      const pending = capture.pending;
      capture.reset();
      replayInto(withheldFrom ?? active, pending);
      withheldFrom = null;
      setReading(false);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      const active = document.activeElement;
      if (scanSuppressed(active)) {
        // A dialog can take the keyboard mid-run — Radix moves focus into it, with no keystroke
        // involved — so the withheld characters go back to the element they were WITHHELD FROM and
        // never into whatever owns the keyboard now. Putting them into the dialog would drop them
        // where its focused control is not a text field, and type them into the explanation the
        // operator is writing where it is.
        //
        // The CURRENT key is deliberately let through to the dialog: that is what AD-32's
        // suppression means, and it is how the operator types into the control in front of them.
        surrender(active);
        return;
      }

      const startsRun = capture.pending.length === 0;
      const symbol = physicalSymbol(event.key, event.code);
      const decision = capture.handleKeyDown({
        key: event.key,
        timeStamp: event.timeStamp,
        chorded: chordedKey(event),
        ...(symbol === undefined ? {} : { symbol }),
      });
      const { held, notify } = apply(decision, active);
      if (held) {
        event.preventDefault();
        event.stopPropagation();
      }
      // After the cancellation, never before it.
      notify?.();
      observe(
        event.key,
        event.code === '' ? null : event.code,
        event.timeStamp,
        'key',
        decision,
        symbol ?? (isCodeCharacter(event.key) ? event.key : null),
        startsRun,
        chordedKey(event),
      );
      // A withheld key inserts nothing, so no `beforeinput` follows it and nothing is owed.
      if (!held && event.key.length === 1 && !chordedKey(event)) {
        lastCharacterKeyAt = event.timeStamp;
      }
      settle(active);
    };

    /**
     * Text that arrived without a usable keystroke.
     *
     * `insertFromPaste` is deliberately skipped: the `paste` listener owns it, and handling both
     * would either count one delivery twice or fight over which of them cancels it.
     */
    /** Applies a text arrival: cancel first, then put back what the field is owed. */
    const applyText = (arrival: TextArrival, event: Event, active: Element | null): void => {
      if (arrival.cancel) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (arrival.decision.action === 'submit') {
        handlers.current.onScan(arrival.decision.code, arrival.decision.origin);
      } else if (arrival.decision.action === 'duplicate') {
        handlers.current.onDuplicate?.(arrival.decision.code);
      }
      replayInto(active, arrival.replay);
    };

    const onBeforeInput = (event: InputEvent): void => {
      const active = document.activeElement;
      if (scanSuppressed(active)) {
        return;
      }
      if (event.inputType === 'insertFromPaste' || event.data === null || event.data === '') {
        return;
      }
      // The echo of a keystroke this capture already saw. Compared by STAMP rather than held as a
      // flag: a flag is only cleared by the next event that reads it, so a key that produced a
      // character the page never inserted — focus on a button, on the body, on the file input —
      // left it armed indefinitely and swallowed the next code a device delivered.
      if (event.timeStamp - lastCharacterKeyAt <= ECHO_WINDOW_MS) {
        return;
      }
      const startsRun = capture.pending.length === 0;
      const arrival = capture.handleText(event.data, event.timeStamp, 'insert');
      applyText(arrival, event, active);
      observe(event.data, null, event.timeStamp, 'insert', arrival.decision, event.data, startsRun);
      settle(active);
    };

    const onPaste = (event: ClipboardEvent): void => {
      const active = document.activeElement;
      if (scanSuppressed(active)) {
        return;
      }
      const text = event.clipboardData?.getData('text') ?? '';
      if (text === '') {
        return;
      }
      const startsRun = capture.pending.length === 0;
      const arrival = capture.handleText(text, event.timeStamp, 'paste');
      applyText(arrival, event, active);
      observe(text, null, event.timeStamp, 'paste', arrival.decision, text, startsRun);
      settle(active);
    };

    /**
     * Suppression is recomputed when focus moves, not only when a key arrives.
     *
     * The indicator has to be honest while nobody is typing: a dialog opening moves focus into it
     * and a dialog closing restores focus, so this is the event that says the keyboard changed
     * hands. Reading it only on a keystroke would leave the indicator claiming to be armed for as
     * long as the operator read the dialog.
     */
    const onFocusChange = (): void => {
      // **Anything withheld goes back before the keyboard changes hands.** Focus can move without a
      // keystroke — a click, a dialog opening — and the run would otherwise continue into the new
      // element: at the old thirty-millisecond window a human could not move focus inside a run, at
      // five hundred a click is ordinary, so the next character would re-point `withheldFrom` and
      // the flush would write the first field's characters into the second.
      if (capture.pending.length > 0) {
        release();
      }
      setSuppressed(scanSuppressed(document.activeElement));
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('beforeinput', onBeforeInput, true);
    document.addEventListener('paste', onPaste, true);
    document.addEventListener('focusin', onFocusChange, true);
    document.addEventListener('focusout', onFocusChange, true);
    onFocusChange();
    return () => {
      clear();
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('beforeinput', onBeforeInput, true);
      document.removeEventListener('paste', onPaste, true);
      document.removeEventListener('focusin', onFocusChange, true);
      document.removeEventListener('focusout', onFocusChange, true);
    };
  }, [enabled]);

  if (!enabled) {
    return 'off';
  }
  if (suppressed) {
    return 'suppressed';
  }
  return reading ? 'reading' : 'ready';
}
